import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useEffect, useMemo } from 'react';

import {
  type OpportunityOfflineActivityDataType,
  useOpportunityOfflineActivity,
} from '@/api/offlineEvents';
import type {
  OpportunityOldType,
  OpportunityStatusOldType,
  OpportunityType,
} from '@/api/opportunity';
import { RangeFilter } from '@/components/filters';
import {
  InstantSearchDataTable,
  type InstantSearchDataTableColumnDef,
  type InstantSearchSortOrder,
  type QueryStateFilter,
  useInstantSearchState,
} from '@/components/InstantSearch';
import { getSolQueryParamsNewFromQueryState } from '@/components/InstantSearch/util/search-util';
import { usePageFilterContext } from '@/components/page';
import { Panel } from '@/components/panels';
import { Link } from '@/components/typography';
import { numberFormat } from '@/helper/numberFormatter';
import { getOpportunityDateRange } from '@/helper/opportunity-date-util';
import { ROUTES } from '@/router';

type Props = {
  oppType: OpportunityStatusOldType;
  opportunity?: OpportunityType | OpportunityOldType;
  defaultSort: {
    sortBy: string;
    sortOrder: InstantSearchSortOrder;
  };
  defaultFilters: QueryStateFilter[];
  startDate?: string;
  endDate?: string;
  isLoading?: boolean;
};

const AttributionOfflineBreakdownTable = ({
  oppType,
  opportunity,
  defaultSort,
  defaultFilters,
  startDate,
  endDate,
  isLoading: isParentLoading = false,
}: Props) => {
  const {
    pageFilters: { audience },
  } = usePageFilterContext();
  const { queryState } = useInstantSearchState();
  const opportunityDateRange = getOpportunityDateRange(opportunity);

  const { offlineActivity, isLoading, error } = useOpportunityOfflineActivity(
    getSolQueryParamsNewFromQueryState(queryState),
    startDate ?? opportunityDateRange.startDate,
    endDate ?? opportunityDateRange.endDate,
    audience,
    opportunity?.id,
  );

  const { updateQueryState } = useInstantSearchState();

  useEffect(() => {
    updateQueryState({
      ...defaultSort,
      filters: defaultFilters,
      page: 1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oppType]);

  const columns = useMemo<InstantSearchDataTableColumnDef<OpportunityOfflineActivityDataType>[]>(
    () => [
      {
        header: t`Category`,
        id: 'campaignTypeId',
        size: 240,
        enableSorting: false,
        minSize: 160,
        flex: 2,
        cell: ({ row }) =>
          `${row.original.campaignType.vendor.name} - ${row.original.campaignType.name}`,
      },
      {
        header: t`Occurrences/Events`,
        id: 'audienceOfflineEvents',
        accessorKey: 'audienceOfflineEvents',
        minSize: 170,
        flex: 1,
        meta: { align: 'end' },
        enableSorting: true,
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => numberFormat(row.original.audienceOfflineEvents),
      },
      {
        header: t`Pipeline Influence`,
        id: 'pipelineInfluenced',
        accessorKey: 'pipelineInfluenced',
        minSize: 120,
        flex: 1,
        meta: { align: 'end' },
        hidden: oppType === 'closedWon',
        enableSorting: true,
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          const text = field.pipelineInfluenced;
          return text === 0 ? (
            numberFormat(text, { isCurrency: true, precision: 0 })
          ) : (
            <Link
              variant="caption1"
              to={ROUTES.opportunityActivityWithParams({
                'o.campaignTypeIdListPipelineInfluence': field.campaignType.id,
              })}
            >
              {numberFormat(text, { isCurrency: true, precision: 0 })}
            </Link>
          );
        },
      },
      {
        header: t`Closed Won Influence`,
        id: 'closedWonInfluenced',
        accessorKey: 'closedWonInfluenced',
        minSize: 120,
        flex: 1,
        meta: { align: 'end' },
        hidden: oppType === 'pipeline',
        enableSorting: true,
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          const text = field.closedWonInfluenced;
          return text === 0 ? (
            numberFormat(text, { isCurrency: true, precision: 0 })
          ) : (
            <Link
              variant="caption1"
              to={ROUTES.opportunityActivityWithParams({
                'o.campaignTypeIdListClosedwonInfluence': field.campaignType.id,
              })}
            >
              {numberFormat(text, { isCurrency: true, precision: 0 })}
            </Link>
          );
        },
      },
      {
        header: t`First-Touch Attribution`,
        id: 'pipelineFirstTouchAttribution',
        accessorKey: 'pipelineFirstTouchAttribution',
        minSize: 120,
        flex: 1,
        meta: { align: 'end' },
        hidden: oppType === 'closedWon',
        enableSorting: true,
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          const text = field.pipelineFirstTouchAttribution;
          return text === 0 ? (
            numberFormat(text, { isCurrency: true, precision: 0 })
          ) : (
            <Link
              variant="caption1"
              to={ROUTES.opportunityActivityWithParams({
                'o.campaignTypeIdListPipelineFirstTouch': field.campaignType.id,
              })}
            >
              {numberFormat(text, { isCurrency: true, precision: 0 })}
            </Link>
          );
        },
      },
      {
        header: t`First-Touch Attribution`,
        id: 'closedWonFirstTouchAttribution',
        accessorKey: 'closedWonFirstTouchAttribution',
        minSize: 120,
        flex: 1,
        meta: { align: 'end' },
        hidden: oppType === 'pipeline',
        enableSorting: true,
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          const text = field.closedWonFirstTouchAttribution;
          return text === 0 ? (
            numberFormat(text, { isCurrency: true, precision: 0 })
          ) : (
            <Link
              variant="caption1"
              to={ROUTES.opportunityActivityWithParams({
                'o.campaignTypeIdListClosedwonFirstTouch': field.campaignType.id,
              })}
            >
              {numberFormat(text, { isCurrency: true, precision: 0 })}
            </Link>
          );
        },
      },
      {
        header: t`Last-Touch Attribution`,
        id: 'pipelineLastTouchAttribution',
        accessorKey: 'pipelineLastTouchAttribution',
        minSize: 120,
        flex: 1,
        meta: { align: 'end' },
        hidden: oppType === 'closedWon',
        enableSorting: true,
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          const text = field.pipelineLastTouchAttribution;
          return text === 0 ? (
            numberFormat(text, { isCurrency: true, precision: 0 })
          ) : (
            <Link
              variant="caption1"
              to={ROUTES.opportunityActivityWithParams({
                'o.campaignTypeIdListPipelineLastTouch': field.campaignType.id,
              })}
            >
              {numberFormat(text, { isCurrency: true, precision: 0 })}
            </Link>
          );
        },
      },
      {
        header: t`Linear Attribution`,
        id: 'pipelineLinearAttribution',
        accessorKey: 'pipelineLinearAttribution',
        minSize: 120,
        flex: 1,
        meta: { align: 'end' },
        hidden: oppType === 'closedWon',
        enableSorting: true,
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          const text = field.pipelineLinearAttribution;
          return text === 0 ? (
            numberFormat(text, { isCurrency: true, precision: 0 })
          ) : (
            <Link
              variant="caption1"
              to={ROUTES.opportunityActivityWithParams({
                'o.campaignTypeIdListPipelineLinear': field.campaignType.id,
              })}
            >
              {numberFormat(text, { isCurrency: true, precision: 0 })}
            </Link>
          );
        },
      },
      {
        header: t`Linear Attribution`,
        id: 'closedWonLinearAttribution',
        accessorKey: 'closedWonLinearAttribution',
        minSize: 120,
        flex: 1,
        meta: { align: 'end' },
        hidden: oppType === 'pipeline',
        enableSorting: true,
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          const text = field.closedWonLinearAttribution;
          return text === 0 ? (
            numberFormat(text, { isCurrency: true, precision: 0 })
          ) : (
            <Link
              variant="caption1"
              to={ROUTES.opportunityActivityWithParams({
                'o.campaignTypeIdListClosedwonLinear': field.campaignType.id,
              })}
            >
              {numberFormat(text, { isCurrency: true, precision: 0 })}
            </Link>
          );
        },
      },
    ],
    [oppType],
  );

  const title = t`Attribution by Non-Digital Event`;

  return (
    <Panel
      title={title}
      infoPopover={{
        title,
        body: (
          <Trans>
            Attribution is shown for each attribution model on this table. This takes into account
            all interactions during each opportunities' timeline from the start of the Opportunity
            influence window to the opportunity creation date (for Pipeline) or closed date (for
            Closed Won).
          </Trans>
        ),
      }}
    >
      <div data-testid="offline-table">
        <InstantSearchDataTable<OpportunityOfflineActivityDataType>
          columns={columns}
          data={offlineActivity}
          loading={isParentLoading || isLoading}
          error={error}
          emptyMessage={<Trans>No non-digital events found.</Trans>}
          getRowId={(record) => record.campaignType.id}
          pagination={false}
          initialColumnPinning={{
            left: ['campaignTypeId'],
          }}
        />
      </div>
    </Panel>
  );
};

export default AttributionOfflineBreakdownTable;
